export type ReportingPeriodCustom = { from?: string; to?: string };

export function getReportingPeriodIso(
    filterPeriod: string,
    custom?: ReportingPeriodCustom
): { dateFrom: string; dateTo: string } {
    const to = new Date();
    const dateTo = to.toISOString();

    if (filterPeriod === 'custom') {
        if (custom?.from && custom?.to) {
            const a = new Date(custom.from);
            const b = new Date(custom.to);
            b.setHours(23, 59, 59, 999);
            return { dateFrom: a.toISOString(), dateTo: b.toISOString() };
        }
        const from = new Date();
        from.setDate(from.getDate() - 30);
        return { dateFrom: from.toISOString(), dateTo };
    }

    if (filterPeriod === 'all') {
        return { dateFrom: new Date(2000, 0, 1).toISOString(), dateTo };
    }

    if (filterPeriod === 'today') {
        const start = new Date(to);
        start.setHours(0, 0, 0, 0);
        return { dateFrom: start.toISOString(), dateTo };
    }

    if (filterPeriod === 'week') {
        const from = new Date(to);
        from.setDate(from.getDate() - 7);
        return { dateFrom: from.toISOString(), dateTo };
    }

    /** Vorige kalenderweek (ma 00:00 – zo 23:59). */
    if (filterPeriod === 'last_week') {
        const day = to.getDay();
        const mondayThisWeek = new Date(to);
        mondayThisWeek.setHours(0, 0, 0, 0);
        mondayThisWeek.setDate(mondayThisWeek.getDate() - (day === 0 ? 6 : day - 1));
        const from = new Date(mondayThisWeek);
        from.setDate(from.getDate() - 7);
        const end = new Date(mondayThisWeek);
        end.setMilliseconds(-1);
        return { dateFrom: from.toISOString(), dateTo: end.toISOString() };
    }

    if (filterPeriod === 'month') {
        const from = new Date(to.getFullYear(), to.getMonth(), 1);
        from.setHours(0, 0, 0, 0);
        return { dateFrom: from.toISOString(), dateTo };
    }

    if (filterPeriod === 'last_month') {
        const from = new Date(to.getFullYear(), to.getMonth() - 1, 1);
        from.setHours(0, 0, 0, 0);
        const end = new Date(to.getFullYear(), to.getMonth(), 0, 23, 59, 59, 999);
        return { dateFrom: from.toISOString(), dateTo: end.toISOString() };
    }

    if (filterPeriod === 'year') {
        const from = new Date(to.getFullYear(), 0, 1);
        from.setHours(0, 0, 0, 0);
        return { dateFrom: from.toISOString(), dateTo };
    }

    const from = new Date(to);
    if (filterPeriod === '30d') from.setDate(from.getDate() - 30);
    else if (filterPeriod === '90d') from.setDate(from.getDate() - 90);
    else if (filterPeriod === 'year_rolling') from.setFullYear(from.getFullYear() - 1);
    else return { dateFrom: new Date(2000, 0, 1).toISOString(), dateTo };
    return { dateFrom: from.toISOString(), dateTo };
}

/** Sollicitatie in periode als created_at óf updated_at in het venster valt. */
export function applicationInReportingPeriod(
    a: { created_at?: string | null; updated_at?: string | null; updatedAt?: string | null },
    filterPeriod: string,
    custom?: ReportingPeriodCustom
): boolean {
    if (filterPeriod === 'all') return true;
    const { dateFrom, dateTo } = getReportingPeriodIso(filterPeriod, custom);
    const from = new Date(dateFrom).getTime();
    const to = new Date(dateTo).getTime();
    const created = new Date(a.created_at || 0).getTime();
    const createdOk = Number.isFinite(created) && created >= from && created <= to;
    const updatedIso = a.updated_at ?? a.updatedAt;
    const updated = updatedIso ? new Date(updatedIso).getTime() : NaN;
    const updatedOk = Number.isFinite(updated) && updated >= from && updated <= to;
    return createdOk || updatedOk;
}

export function createdAtInReportingPeriod(
    iso: string | undefined,
    filterPeriod: string,
    custom?: ReportingPeriodCustom
): boolean {
    if (filterPeriod === 'all') return true;
    if (!iso) return false;
    const { dateFrom, dateTo } = getReportingPeriodIso(filterPeriod, custom);
    const t = new Date(iso).getTime();
    return t >= new Date(dateFrom).getTime() && t <= new Date(dateTo).getTime();
}

export function reportingPeriodLabel(filterPeriod: string): string {
    switch (filterPeriod) {
        case 'all':
            return 'Altijd';
        case 'today':
            return 'Vandaag';
        case 'week':
            return 'Afgelopen 7 dagen';
        case 'last_week':
            return 'Vorige week (ma–zo)';
        case 'month':
            return 'Deze maand';
        case 'last_month':
            return 'Vorige maand';
        case 'year':
            return 'Dit jaar';
        case 'year_rolling':
            return 'Afgelopen 12 maanden';
        case '30d':
            return 'Laatste 30 dagen';
        case '90d':
            return 'Laatste 3 maanden';
        case 'custom':
            return 'Aangepast';
        default:
            return 'Altijd';
    }
}

const nlDateFmt = new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
});

/** Leesbare datumrange voor filterknop en KPI-context. */
export function reportingPeriodRangeLabel(
    filterPeriod: string,
    custom?: ReportingPeriodCustom
): string {
    if (filterPeriod === 'all') return 'Geen periodebeperking';
    if (filterPeriod === 'custom') {
        if (custom?.from && custom?.to) {
            const a = new Date(custom.from);
            const b = new Date(custom.to);
            if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime())) {
                return `${nlDateFmt.format(a)} – ${nlDateFmt.format(b)}`;
            }
        }
        return 'Kies van- en tot-datum';
    }
    const { dateFrom, dateTo } = getReportingPeriodIso(filterPeriod, custom);
    const a = new Date(dateFrom);
    const b = new Date(dateTo);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';
    return `${nlDateFmt.format(a)} – ${nlDateFmt.format(b)}`;
}

export function getEffectiveReportingFilterDays(filterPeriod: string, custom?: ReportingPeriodCustom): number {
    const { dateFrom, dateTo } = getReportingPeriodIso(filterPeriod, custom);
    if (filterPeriod === 'all') return 365;
    const ms = new Date(dateTo).getTime() - new Date(dateFrom).getTime();
    return Math.max(1, ms / 86400000);
}
